// Logger utility for better error handling and debugging
import { AxiosError } from 'axios';

enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Define log argument type - allows any type for logging flexibility
type LogArgs = unknown[];

class Logger {
  private static instance: Logger;
  private logLevel: LogLevel;

  private constructor() {
    // Set log level based on environment
    this.logLevel = process.env.NODE_ENV === 'production' ? LogLevel.WARN : LogLevel.DEBUG;
  }

  public static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.logLevel;
  }

  public debug(message: string, ...args: LogArgs): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      console.debug(`[DEBUG] ${message}`, ...args);
    }
  }

  public info(message: string, ...args: LogArgs): void {
    if (this.shouldLog(LogLevel.INFO)) {
      console.info(`[INFO] ${message}`, ...args);
    }
  }

  public warn(message: string, ...args: LogArgs): void {
    if (this.shouldLog(LogLevel.WARN)) {
      console.warn(`[WARN] ${message}`, ...args);
    }
  }

  public error(message: string, error?: Error | unknown, ...args: LogArgs): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      console.error(`[ERROR] ${message}`, error, ...args);

      // In production, you might want to send errors to a monitoring service
      if (process.env.NODE_ENV === 'production' && error) {
        // TODO: Integrate with error monitoring service like Sentry
        // Sentry.captureException(error);
      }
    }
  }

  // Special method for API errors
  public apiError(operation: string, error: AxiosError): void {
    const responseData = error?.response?.data as { detail?: string } | undefined;
    const errorMessage = responseData?.detail || error?.message || 'Unknown API error';
    this.error(`API Error during ${operation}:`, {
      error: errorMessage,
      status: error?.response?.status,
      url: error?.config?.url,
      method: error?.config?.method
    });
  }
}

// Export singleton instance
export const logger = Logger.getInstance();

// Convenience functions
export const logDebug = (message: string, ...args: LogArgs) => logger.debug(message, ...args);
export const logInfo = (message: string, ...args: LogArgs) => logger.info(message, ...args);
export const logWarn = (message: string, ...args: LogArgs) => logger.warn(message, ...args);
export const logError = (message: string, error?: Error | unknown, ...args: LogArgs) => logger.error(message, error, ...args);
export const logApiError = (operation: string, error: AxiosError) => logger.apiError(operation, error);